import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Send, Mail, Loader2, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const subjects = [
  { value: 'demo', label: 'Demande de demo' },
  { value: 'presse', label: 'Presse / Media' },
  { value: 'investisseur', label: 'Investisseur' },
  { value: 'partenariat', label: 'Partenariat' },
  { value: 'autre', label: 'Autre' },
];

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', company: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Erreur lors de l\'envoi');
      }
      setSent(true);
    } catch (err) {
      setError(err.message || 'Impossible de contacter le serveur');
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      <Helmet>
        <title>Contact | Cloneia</title>
        <meta name="description" content="Contactez l'équipe Cloneia. Presse, investisseurs, partenariats ou demande de démo — nous vous répondons sous 24h." />
      </Helmet>

      <Header />

      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left - Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-4">Contact</p>
              <h1 className="text-4xl md:text-5xl font-semibold text-[#f5f5f5] tracking-tight mb-6">
                Parlons-en.
              </h1>
              <p className="text-[#a1a1aa] text-base md:text-lg mb-10 leading-relaxed">
                Que vous soyez dirigeant, investisseur, journaliste ou simplement curieux — nous répondons à toutes les demandes sous 24h.
              </p>

              {/* Team link */}
              <div className="mt-10 p-5 rounded-xl border border-white/5 bg-[#0f0f17]">
                <p className="text-[#a1a1aa] text-sm mb-3">Découvrez qui nous sommes</p>
                <Link
                  to="/equipe"
                  className="inline-flex items-center gap-2 text-[#3b82f6] font-medium text-sm hover:gap-3 transition-all"
                  data-testid="contact-team-link"
                >
                  Voir l'équipe dirigeante
                  <span>&rarr;</span>
                </Link>
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              {sent ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="h-full flex items-center justify-center"
                >
                  <div className="text-center p-10 rounded-2xl border border-green-500/20 bg-green-500/5">
                    <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-400" />
                    </div>
                    <h2 className="text-xl font-semibold text-[#f5f5f5] mb-2">Message envoyé !</h2>
                    <p className="text-[#71717a] text-sm">Nous vous répondons sous 24h.</p>
                  </div>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#a1a1aa] text-sm font-medium mb-1.5">Nom *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-[#0f0f17] border border-white/10 text-[#f5f5f5] text-sm focus:outline-none focus:border-[#3b82f6] placeholder-[#52525b] transition-colors"
                        placeholder="Votre nom"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="block text-[#a1a1aa] text-sm font-medium mb-1.5">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-[#0f0f17] border border-white/10 text-[#f5f5f5] text-sm focus:outline-none focus:border-[#3b82f6] placeholder-[#52525b] transition-colors"
                        placeholder="votre@email.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#a1a1aa] text-sm font-medium mb-1.5">Objet *</label>
                    <select
                      name="subject"
                      required
                      value={form.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-[#0f0f17] border border-white/10 text-[#f5f5f5] text-sm focus:outline-none focus:border-[#3b82f6] transition-colors"
                      data-testid="contact-subject"
                    >
                      <option value="" className="text-[#52525b]">Sélectionnez un motif</option>
                      {subjects.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#a1a1aa] text-sm font-medium mb-1.5">Entreprise</label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-[#0f0f17] border border-white/10 text-[#f5f5f5] text-sm focus:outline-none focus:border-[#3b82f6] placeholder-[#52525b] transition-colors"
                      placeholder="Votre entreprise (optionnel)"
                      data-testid="contact-company"
                    />
                  </div>

                  <div>
                    <label className="block text-[#a1a1aa] text-sm font-medium mb-1.5">Message *</label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-[#0f0f17] border border-white/10 text-[#f5f5f5] text-sm focus:outline-none focus:border-[#3b82f6] placeholder-[#52525b] transition-colors resize-none"
                      placeholder="Votre message..."
                      data-testid="contact-message"
                    />
                  </div>

                  {error && (
                    <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="contact-error">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#3b82f6] hover:bg-[#2563eb] text-white font-medium transition-all disabled:opacity-60"
                    data-testid="contact-submit"
                  >
                    {sending ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours...</>
                    ) : (
                      <><Send className="w-4 h-4" /> Envoyer</>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
